import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { readPosition, loadGeoJournal, saveGeoJournal } from './geolocationCapture';
export { DENIED_DETAIL, GEO_ACK_KEY, geoAcknowledged, acknowledgeGeo } from './geolocationCapture';

export const useGeolocationCapture = (revision = 0, userId) => {
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState('');
  const [journal, setJournal] = useState(() => loadGeoJournal(userId));
  const [lastEvent, setLastEvent] = useState(null);
  const currentSettings = useRef(null);
  const settingsRequest = useRef(0);
  const completedSettingsRequest = useRef(0);
  const lastKnownEnabled = useRef(false);
  const record = useCallback((entry, phase, values) => {
    const event = { entry_id: entry.entry_id, phase, pointing_at: phase === 'start' ? entry.started_at : entry.ended_at,
      at: Date.now(), ...values };
    setLastEvent(event);
    setJournal(previous => {
      const next = Object.fromEntries(Object.entries({ ...previous, [`${entry.entry_id}:${phase}`]: event })
        .filter(([, v]) => Date.now() - v.at < 86400000).sort((a, b) => b[1].at - a[1].at).slice(0, 40));
      saveGeoJournal(userId, next); return next;
    });
  }, [userId]);
  const readSettings = useCallback(async () => {
    const sequence = ++settingsRequest.current;
    try {
      const { data } = await axios.get(`${API}/internal-staff/geolocation-settings`, { withCredentials: true, timeout: 6000 });
      if (typeof data.enabled !== 'boolean') throw new Error('Activation non vérifiable');
      // Une lecture plus récente encore en vol ne doit pas masquer cette réponse valide.
      if (sequence >= completedSettingsRequest.current) {
        completedSettingsRequest.current = sequence;
        lastKnownEnabled.current = data.enabled;
        currentSettings.current = data; setSettings(data); setSettingsError('');
      }
      return data;
    } catch (error) {
      if (sequence >= completedSettingsRequest.current) {
        completedSettingsRequest.current = sequence;
        currentSettings.current = null; setSettings(null); setSettingsError('Activation de la localisation non vérifiable.');
      }
      throw error;
    }
  }, []);
  useEffect(() => { readSettings().catch(() => {}); }, [revision, readSettings]);

  // Relire l'activation pour CE clic : une erreur de lecture n'est jamais assimilée à OFF.
  const prepare = useCallback(async (entry, phase) => {
    record(entry, phase, { stage: 'settings', message: 'Vérification de l’activation…' });
    try {
      const latest = await readSettings();
      record(entry, phase, { stage: latest.enabled ? 'notice' : 'disabled', enabled: latest.enabled,
        message: latest.enabled ? 'Localisation activée. En attente de la demande ponctuelle.' : 'Localisation désactivée dans l’application : aucune demande effectuée.' });
      return latest.enabled;
    } catch (error) {
      record(entry, phase, { stage: 'settings_error', enabled: lastKnownEnabled.current, http_status: error.response?.status,
        message: 'Activation non vérifiable : aucune position demandée. Le pointage est enregistré.' });
      return false;
    }
  }, [readSettings, record]);
  const send = useCallback(async (entry, phase, payload, browserCode) => {
    if (!currentSettings.current?.enabled) {
      record(entry, phase, { stage: currentSettings.current ? 'disabled' : 'settings_error', enabled: lastKnownEnabled.current, message: 'Aucun envoi de position : activation non confirmée ou désactivée.' }); return null;
    }
    const info = { enabled: true, browser_status: payload.status, browser_code: browserCode };
    record(entry, phase, { ...info, stage: 'sending', message: 'Enregistrement de la position…' });
    try {
      const { data } = await axios.post(`${API}/internal-staff/clock/${entry.entry_id}/location`, { phase, ...payload }, { withCredentials: true, timeout: 10000 });
      const saved = data[`${phase}_location`];
      if (data.entry_id !== entry.entry_id || !saved) throw new Error('Réponse de rattachement incomplète');
      record(entry, phase, { ...info, stage: 'saved', http_status: 200, server_status: saved.status,
        message: 'Résultat de localisation enregistré sur ce pointage.' });
      return data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      record(entry, phase, { ...info, stage: 'send_error', http_status: error.response?.status,
        message: typeof detail === 'string' ? `Position non enregistrée : ${detail}` : 'Enregistrement de la position non confirmé (réseau ou réponse serveur). Le pointage est enregistré.' });
      return null;
    }
  }, [record]);
  const capture = useCallback(async (entry, phase) => {
    if (!currentSettings.current?.enabled) {
      record(entry, phase, { stage: currentSettings.current ? 'disabled' : 'settings_error', enabled: lastKnownEnabled.current, message: 'Aucune demande de position : activation non confirmée ou désactivée.' }); return null;
    }
    record(entry, phase, { stage: 'browser', enabled: true, message: 'Demande ponctuelle au navigateur…' });
    const { payload, browserCode } = await readPosition();
    return send(entry, phase, payload, browserCode);
  }, [record, send]);
  const declineNotice = useCallback((entry, phase) => send(entry, phase,
    { status: 'non_relevee', detail: 'Le salarié a choisi de ne pas relever sa position' }, null), [send]);
  return { enabled: settings?.enabled === true, settings, settingsError, prepare, capture, declineNotice, journal, lastEvent };
};